import { useState } from 'react';
import { useNavigate } from './useNavigate';
import { useRouteParams } from './useRouteParams';
import { downloadFile } from '../../CommonUtils/downloadFile';
import { toast } from '../../CommonComponents/OPL/Toast/Toast';
import { useDownloadFileMutation } from '../api/useDownloadFileMutation';
import { useDocumentVaultContext } from '../context/useDocumentVaultContext';
import { EntityDTO, EntityTypeDTO } from '../DocumentVault.meta';

export interface UseEntityListActions {
  onEntityPress(entity: EntityDTO): void;
  setCurrentVideoEntity(entity: EntityDTO | null): void;
}

export interface UseBaseEntityListResult {
  isDownloadingFile: boolean;
  currentVideoEntity: EntityDTO | null;
  actions: UseEntityListActions;
}

export const useBaseEntityList = (): UseBaseEntityListResult => {
  const [currentVideoEntity, setCurrentVideoEntity] = useState<EntityDTO | null>(null);
  const { clientId } = useRouteParams();

  const { mutateAsync: downloadFileBlob, isLoading: isDownloadingFile } = useDownloadFileMutation();

  const navigate = useNavigate();

  const { setSelectedEntity, setForceYear } = useDocumentVaultContext();

  const onEntityPress = async (entity: EntityDTO) => {
    switch (entity.type) {
      case EntityTypeDTO.File:
        try {
          const { fileName, fileBlob } = await downloadFileBlob({
            clientId,
            path: entity.path,
            externalFileId: entity.externalFileId,
            providerType: entity.providerType,
          });

          return downloadFile(fileName || entity.name, fileBlob);
        } catch {
          return toast.error('There was an error downloading this file. Please try again later.');
        }

      case EntityTypeDTO.Folder: {
        const canNavigate = setSelectedEntity(entity);

        if (!canNavigate) {
          return null;
        }

        if (entity.forceYear) {
          setForceYear(entity.forceYear);
        }

        return navigate({
          path: entity.path ? encodeURIComponent(entity.path) : '',
          providerType: entity.providerType,
          filterableByYear: entity.filterableByYear,
        });
      }
      case EntityTypeDTO.Video:
        return setCurrentVideoEntity(entity);

      default:
        return toast.error('The selected entity type is not supported.');
    }
  };

  return {
    isDownloadingFile,
    currentVideoEntity,
    actions: {
      onEntityPress,
      setCurrentVideoEntity,
    },
  };
};
